import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { Button, Select, Box } from '@nerdwallet/currency';
import AwardsBadgeDropdownOutput from '../../lib/awards-badge-dropdown/awards-badge-dropdown-output';

import styles from './awards-badge-dropdown.module.less';
import { DATA_SOURCE } from '../../lib/awards-badge-dropdown/data';
import {
  stringSelectSpec,
  stringInputSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';
import {
  detectContrast,
  isHexColor,
} from '../../lib/awards-badge-dropdown/lib/color-helper';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container/universal-container';
import UniversalText from '../universal-text/universal-text';

const AwardsBadgeDropdownV2 = ({
  title,
  subtitle,
  hexColor,
  awardsYear,
  container,
  boldAwardsSelect,
}) => {
  const [selectedCompany, setSelectedCompany] = useState('N/A');
  const data = DATA_SOURCE[awardsYear];
  const validHexColor = isHexColor(hexColor) ? hexColor : '#FFF';
  const selectedClassName = detectContrast(validHexColor, 0.7, styles.light);
  const selectStyle = {
    backgroundColor: validHexColor,
    ...(boldAwardsSelect && { fontWeight: 'bold' }),
  };

  const options = data?.map((company) => {
    return {
      label: `${company.name}`,
      value: `${company.id}`,
    };
  });

  const isCompanyInAwardsYear = !!options
    .filter((item) => item.value !== 'N/A')
    .find((item) => item.value === selectedCompany);

  return (
    <UniversalContainer {...container} data-testid="AwardsBadgeDropdownV2">
      {title?.text && <UniversalText {...title} />}
      {subtitle?.text && <UniversalText {...subtitle} textType="text" />}
      <Box
        className={classNames([
          styles.awardsDropdownContent,
          classes('margin-top-0'),
          classes('margin-bottom-3'),
          classes('margin-horizontal-auto'),
          classes('display-flex'),
          classes('flex-direction-column'),
          classes('align-items-center'),
        ])}
      >
        <Select
          id="dropdown"
          value={selectedCompany}
          className={selectedClassName}
          style={selectStyle}
          onChange={(e) => setSelectedCompany(e.target.value)}
          options={options}
        />
      </Box>
      {selectedCompany && isCompanyInAwardsYear ? (
        <AwardsBadgeDropdownOutput
          id={selectedCompany}
          awardsYear={awardsYear}
        />
      ) : (
        <Box
          className={classNames([
            classes('margin-horizontal-auto'),
            classes('margin-top-4'),
            classes('text-align-center'),
          ])}
        >
          <Button primary disabled>
            DOWNLOAD FILES
          </Button>
        </Box>
      )}
    </UniversalContainer>
  );
};

AwardsBadgeDropdownV2.propTypes = {
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  hexColor: PropTypes.string,
  awardsYear: PropTypes.string,
  boldAwardsSelect: PropTypes.bool,
};

AwardsBadgeDropdownV2.defaultProps = {
  hexColor: '#FFF',
  awardsYear: '2021',
};

AwardsBadgeDropdownV2.getEditingConfig = (componentDocument) => [
  ...UniversalContainer.getEditingConfig(
    componentDocument,
    'Container',
    ['backgroundColor', 'backgroundImage', 'border', 'margin', 'padding'],
    'container'
  ),
  ...UniversalText.getEditingConfig(
    componentDocument,
    'Title',
    [
      'fontWeight',
      'groupButton',
      'margin',
      'padding',
      'textChronicle',
      'textColor',
      'textSize',
      'textType',
    ],
    'title'
  ),
  ...UniversalText.getEditingConfig(
    componentDocument,
    'Subtitle',
    [
      'fontWeight',
      'groupButton',
      'margin',
      'padding',
      'textChronicle',
      'textColor',
      'textSize',
      'textType',
    ],
    'subtitle',
    { text: { textType: 'text' } }
  ),
  stringInputSpec(
    'hexColor',
    'Dropdown Background HEX Color (eg: "#D8D9DA" - Default is #FFF)'
  ),
  boolCheckboxSpec(
    'boldAwardsSelect',
    'Add Bold Style To Dropdown Placeholder?'
  ),
  stringSelectSpec('awardsYear', 'Awards Year (default is 2021)', [
    { label: '2021', value: '2021' },
    { label: '2020', value: '2020' },
    { label: '2022', value: '2022' },
    { label: '2023', value: '2023' },
    { label: '2024', value: '2024' },
  ]),
];

export default AwardsBadgeDropdownV2;
